// TUT-450 — os membros do escritório: lista, cota de armazenamento, convites, ativação, ações em lote, senha, e o registro de atividade.
import { demoApi } from "../demo-data";
import type {
  AccessOverview,
  AuditEventGroup,
  AgrupamentoDaAtividade,
  AuditEventListResponse,
  AcaoEmLote,
  ResultadoDoLote,
  CreateInvitationInput,
  InvitationSummary,
  InviteResult,
  InviteUserResult,
  PasswordResetTriggerResult,
  TenantMember,
  ExternalStorageQuotaSummary,
  StorageUpgradeRequestResult,
  UserGroup,
} from "../types";
import { comFatia, request } from "./infra";
import type { FatiaPedida } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDosMembros = {
  users(fatia: FatiaPedida = {}, search = ""): Promise<AccessOverview> {
    if (useDemoApi) return demoApi.users(fatia, search);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    return request<AccessOverview>(`/users${comFatia(params, fatia)}`);
  },
  updateExternalStorageQuota(
    membershipId: string,
    quotaBytes: number,
  ): Promise<ExternalStorageQuotaSummary> {
    return useDemoApi
      ? demoApi.updateExternalStorageQuota(membershipId, quotaBytes)
      : request<ExternalStorageQuotaSummary>(
          `/users/storage/external/${encodeURIComponent(membershipId)}`,
          {
            method: "PATCH",
            body: JSON.stringify({ quotaBytes }),
          },
        );
  },
  requestStorageUpgrade(
    requestedQuotaBytes: number | null,
  ): Promise<StorageUpgradeRequestResult> {
    return useDemoApi
      ? demoApi.requestStorageUpgrade(requestedQuotaBytes)
      : request<StorageUpgradeRequestResult>(
          "/users/storage/upgrade-requests",
          {
            method: "POST",
            body: JSON.stringify({ requestedQuotaBytes }),
          },
        );
  },
  /** TUT-544 — o convite leva os GRUPOS e nenhum papel (a API recusa `role`). */
  inviteUser(input: CreateInvitationInput): Promise<InviteUserResult> {
    if (useDemoApi) return demoApi.inviteUser(input);
    const { email, displayName, groupIds } = input;
    return request<InviteUserResult>("/users/invitations", {
      method: "POST",
      body: JSON.stringify({ email, displayName, groupIds }),
    });
  },
  /** TUT-563e (E9) — link novo (o anterior morre), validade renovada e o e-mail de novo. */
  resendInvitation(id: string): Promise<InviteResult> {
    return useDemoApi
      ? demoApi.resendInvitation(id)
      : request<InviteResult>(`/users/invitations/${encodeURIComponent(id)}/resend`, { method: "POST" });
  },
  revokeInvitation(id: string): Promise<InvitationSummary> {
    return useDemoApi
      ? demoApi.revokeInvitation(id)
      : request<InvitationSummary>(
          `/users/invitations/${encodeURIComponent(id)}/revoke`,
          { method: "POST" },
        );
  },
  /** TUT-485 (D19) — o administrador corrige o nome de quem é só do escritório dele. */
  renameMember(membershipId: string, name: string): Promise<{ name: string }> {
    return useDemoApi
      ? demoApi.renameMember(membershipId, name)
      : request<{ name: string }>(
          `/users/${encodeURIComponent(membershipId)}/name`,
          { method: "PATCH", body: JSON.stringify({ name }) },
        );
  },
  /** TUT-485 (D19) — pede a troca do e-mail; quem confirma é a própria pessoa. */
  requestMemberEmailChange(
    membershipId: string,
    newEmail: string,
  ): Promise<{ message: string }> {
    return useDemoApi
      ? demoApi.requestMemberEmailChange(membershipId, newEmail)
      : request<{ message: string }>(
          `/users/${encodeURIComponent(membershipId)}/email`,
          { method: "POST", body: JSON.stringify({ newEmail }) },
        );
  },
  setMemberActive(
    membershipId: string,
    active: boolean,
  ): Promise<TenantMember> {
    const action = active ? "activate" : "deactivate";
    return useDemoApi
      ? demoApi.setMemberActive(membershipId, active)
      : request<TenantMember>(
          `/users/${encodeURIComponent(membershipId)}/${action}`,
          { method: "POST" },
        );
  },
  /**
   * TUT-288d (fatia 2) — a mesma ação para várias pessoas. Na demonstração, o
   * lote passa pelas MESMAS portas de linha, uma a uma, como o servidor faz.
   */
  async loteDeUsuarios(input: {
    action: AcaoEmLote;
    membershipIds: string[];
  }): Promise<{ items: ResultadoDoLote[] }> {
    if (!useDemoApi) {
      return request<{ items: ResultadoDoLote[] }>("/users/batch", {
        method: "POST",
        body: JSON.stringify(input),
      });
    }
    const items: ResultadoDoLote[] = [];
    for (const membershipId of input.membershipIds) {
      try {
        if (input.action === "password-reset") {
          const { resetUrl, emailDelivered } = await demoApi.triggerMemberPasswordReset(membershipId);
          items.push({ membershipId, ok: true, emailDelivered, ...(emailDelivered ? {} : { resetUrl }) });
        } else {
          await demoApi.setMemberActive(membershipId, input.action === "activate");
          items.push({ membershipId, ok: true });
        }
      } catch (erro) {
        items.push({ membershipId, ok: false, error: erro instanceof Error ? erro.message : "Recusado." });
      }
    }
    return { items };
  },
  /** TUT-288d (fatia 2) — soma pessoas a um grupo, sem tirar ninguém. */
  async adicionarAoGrupo(
    groupId: string,
    userIds: string[],
  ): Promise<{ group: UserGroup; added: number }> {
    if (!useDemoApi) {
      return request<{ group: UserGroup; added: number }>(
        `/user-groups/${encodeURIComponent(groupId)}/members`,
        { method: "POST", body: JSON.stringify({ userIds }) },
      );
    }
    const grupo = (await demoApi.userGroups()).items.find((item) => item.id === groupId);
    if (!grupo) throw new Error("Grupo não encontrado.");
    const atuais = new Set(grupo.members.map((membro) => membro.userId));
    const novos = [...new Set(userIds)].filter((userId) => !atuais.has(userId));
    if (novos.length === 0) return { group: grupo, added: 0 };
    const group = await demoApi.updateUserGroup(groupId, { userIds: [...atuais, ...novos] });
    return { group, added: novos.length };
  },
  triggerMemberPasswordReset(
    membershipId: string,
  ): Promise<PasswordResetTriggerResult> {
    return useDemoApi
      ? demoApi.triggerMemberPasswordReset(membershipId)
      : request<PasswordResetTriggerResult>(
          `/users/${encodeURIComponent(membershipId)}/password-reset`,
          { method: "POST" },
        );
  },
  auditEvents(
    search = "",
    fatia: FatiaPedida = {},
    // TUT-335 — os eventos de UM grupo (a chave que `auditEventGroups` deu).
    grupo: { ator?: string; acao?: string } = {},
  ): Promise<AuditEventListResponse> {
    if (useDemoApi) return demoApi.auditEvents(search, fatia, grupo);
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (grupo.ator) params.set("ator", grupo.ator);
    if (grupo.acao) params.set("acao", grupo.acao);
    return request<AuditEventListResponse>(
      `/audit-events${comFatia(params, fatia)}`,
    );
  },
  /** TUT-335 — o registro agrupado por quem fez ou pelo que foi feito. */
  auditEventGroups(
    por: AgrupamentoDaAtividade,
    search = "",
  ): Promise<{ items: AuditEventGroup[] }> {
    if (useDemoApi) return demoApi.auditEventGroups(por, search);
    const params = new URLSearchParams({ por });
    if (search) params.set("search", search);
    return request<{ items: AuditEventGroup[] }>(`/audit-events/groups?${params}`);
  },
};
